import useMediaQuery from '@cannect/hooks/useMediaQuery'
import { MOBILE_WIDTH } from '@cannect/utils/constants'
import { AnimatePresence, motion } from 'framer-motion'
import { CreditCard, Plus, Receipt, Wallet } from 'lucide-react'
import React, { useMemo } from 'react'
import * as S from './styles'

interface PaymentMethodSelectorProps {
  selectedMethod: string | null
  onSelectMethod: (method: string) => void
  totalAmount: number
  disabledPix?: boolean
  disabledCreditCard?: boolean
  disabledBoleto?: boolean
  hasMultipleMethods?: boolean
  applyPixDiscountOrder?: boolean
  isPagaleveEligible?: boolean
  isCheckingPagaleve?: boolean
}

const PaymentMethodSelector: React.FC<PaymentMethodSelectorProps> = ({
  selectedMethod,
  onSelectMethod,
  totalAmount,
  disabledPix,
  disabledCreditCard,
  disabledBoleto,
  hasMultipleMethods,
  applyPixDiscountOrder,
  isPagaleveEligible = false,
  isCheckingPagaleve = false
}) => {
  const isMobile = useMediaQuery(MOBILE_WIDTH)

  const applyPixDiscount = import.meta.env.VITE_APP_APPLY_PIX_DISCOUNT === 'enabled' && applyPixDiscountOrder
  const pixDiscountPercentage = Number(import.meta.env.VITE_APP_PIX_DISCOUNT_PERCENTAGE)

  const singleMethods = useMemo(
    () =>
      [
        {
          id: 'credit_card',
          title: 'Cartão de Crédito',
          icon: <CreditCard size={24} />,
          description: 'Pague com seu cartão de crédito',
          disabled: disabledCreditCard
        },
        {
          id: 'pix',
          title: 'Pix',
          icon: <Wallet size={24} />,
          description: 'Pagamento instantâneo',
          disabled: disabledPix
        },
        {
          id: 'pagaleve',
          title: 'PIX Parcelado',
          icon: <Wallet size={24} />,
          description: 'Parcele em até 4x',
          disabled: disabledPix
        },
        {
          id: 'boleto',
          title: 'Boleto Bancário',
          icon: <Receipt size={24} />,
          description: 'Vencimento em 3 dias úteis',
          disabled: disabledBoleto
        }
      ].filter((method) => {
        if (method.id === 'pagaleve') {
          return isPagaleveEligible
        }
        return true
      }),
    [disabledCreditCard, disabledPix, disabledBoleto, isPagaleveEligible]
  )

  const combinedMethods = [
    {
      id: 'credit_card_credit_card',
      title: 'Dois Cartões',
      icon: <CreditCard size={24} />,
      secondIcon: <CreditCard size={24} />,
      description: 'Divida entre dois cartões',
      disabled: disabledCreditCard
    },
    {
      id: 'credit_card_pix',
      title: 'Cartão + Pix',
      icon: <CreditCard size={24} />,
      secondIcon: <Wallet size={24} />,
      description: 'Parte no cartão, parte no Pix',
      disabled: disabledCreditCard || disabledPix
    },
    {
      id: 'credit_card_boleto',
      title: 'Cartão + Boleto',
      icon: <CreditCard size={24} />,
      secondIcon: <Receipt size={24} />,
      description: 'Parte no cartão, parte no boleto',
      disabled: disabledCreditCard || disabledBoleto
    }
  ]

  const applyPaymentDiscount = (amount: number, method: string) => {
    if (applyPixDiscount && method === 'pix') {
      const discount = amount * (pixDiscountPercentage / 100)
      return amount - discount
    }
    return amount
  }

  const isLoadingData = !totalAmount || totalAmount === 0

  if (isLoadingData) {
    return (
      <S.PaymentSelectorContainer>
        <S.PaymentSectionTitle>Escolha como deseja pagar</S.PaymentSectionTitle>
        <div style={{ padding: '40px 0', textAlign: 'center' }}>
          <div
            style={{
              width: '50px',
              height: '50px',
              border: '4px solid #f3f3f3',
              borderTop: '4px solid #748351',
              borderRadius: '50%',
              margin: '0 auto',
              animation: 'spin 1s linear infinite'
            }}
          />
          <p style={{ marginTop: '20px', color: '#666', fontSize: '14px' }}>Carregando opções de pagamento...</p>
        </div>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </S.PaymentSelectorContainer>
    )
  }

  if (isCheckingPagaleve) {
    return (
      <S.PaymentSelectorContainer>
        <S.PaymentSectionTitle>Escolha como deseja pagar</S.PaymentSectionTitle>
        <S.PaymentOptionsSection>
          <S.PaymentOptionsGroup>
            <S.GroupTitle>Pagamento único</S.GroupTitle>
            <S.OptionsRow>
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  style={{
                    height: isMobile ? '80px' : '120px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '8px',
                    animation: 'pulse 1.5s ease-in-out infinite'
                  }}
                />
              ))}
            </S.OptionsRow>
          </S.PaymentOptionsGroup>
        </S.PaymentOptionsSection>
      </S.PaymentSelectorContainer>
    )
  }

  return (
    <S.PaymentSelectorContainer>
      <S.PaymentSectionTitle>Escolha como deseja pagar</S.PaymentSectionTitle>

      <S.PaymentOptionsSection>
        <S.PaymentOptionsGroup>
          <S.GroupTitle>Pagamento único</S.GroupTitle>
          <S.OptionsRow>
            {singleMethods.map((method) => (
              <S.PaymentOption
                key={method.id}
                isSelected={selectedMethod === method.id}
                onClick={() => onSelectMethod(method.id)}
                disabled={method.disabled}
                isMobile={isMobile}>
                <div className="icon-container">{method.icon}</div>
                <div className="method-info">
                  <h3>{method.title}</h3>
                  <p>{method.description}</p>
                </div>
                <div className="amount">
                  {applyPixDiscount && method.id === 'pix' && (
                    <div className="discount-container">
                      <span className="discount-label">-{pixDiscountPercentage}%</span>
                      <span className="discount-value">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL'
                        }).format(totalAmount)}
                      </span>
                    </div>
                  )}
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL'
                  }).format(applyPaymentDiscount(totalAmount, method.id))}
                </div>
                <AnimatePresence>
                  {selectedMethod === method.id && (
                    <motion.div
                      className="check-indicator"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      exit={{ scale: 0 }}
                    />
                  )}
                </AnimatePresence>
              </S.PaymentOption>
            ))}
          </S.OptionsRow>
        </S.PaymentOptionsGroup>

        {hasMultipleMethods && (
          <S.PaymentOptionsGroup>
            <S.GroupTitle>Pagamento combinado</S.GroupTitle>
            <S.OptionsRow>
              {combinedMethods.map((method) => (
                <S.PaymentOption
                  key={method.id}
                  isSelected={selectedMethod === method.id}
                  onClick={() => onSelectMethod(method.id)}
                  disabled={method.disabled}
                  isMobile={isMobile}>
                  <div className="combined-icons">
                    <div className="icon-container">{method.icon}</div>
                    <Plus size={16} className="plus-icon" />
                    <div className="icon-container">{method.secondIcon}</div>
                  </div>
                  <div className="method-info">
                    <h3>{method.title}</h3>
                    <p>{method.description}</p>
                  </div>
                  <div className="amount">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(totalAmount)}
                  </div>
                  <AnimatePresence>
                    {selectedMethod === method.id && (
                      <motion.div
                        className="check-indicator"
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        exit={{ scale: 0 }}
                      />
                    )}
                  </AnimatePresence>
                </S.PaymentOption>
              ))}
            </S.OptionsRow>
          </S.PaymentOptionsGroup>
        )}
      </S.PaymentOptionsSection>
    </S.PaymentSelectorContainer>
  )
}

export default PaymentMethodSelector
